import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
    Box,
    FormControl,
    FormHelperText,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { sectionTitleSx } from '../../styles/typography';
import { analyticsMenuPaperSx, getAnalyticsUnsavedControlStyle } from '../../styles/analyticsMsiStyles';
import { APP_STYLE_PALETTE } from '../../styles/msiStyleTokens';

const stopActionMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
};
const stopActionClick = (event) => event.stopPropagation();

// This view knows only option values and presentation. Its caller owns saved
// portfolio checks and all actions; controlled open state lets those close it.
export default function PortfolioChooser({
    label,
    labelId,
    value = '',
    selectedLabel = '',
    selectedTitle,
    selectedIndicators = null,
    groups = [],
    open,
    onOpen,
    onClose,
    onSelect,
    onReselect,
    hideTitle = false,
    compact = false,
    unsaved = false,
    disabled = false,
    helperText = null,
    emptyLabel = 'No data available',
}) {
    const generatedLabelId = useId();
    const inputLabelId = labelId ?? generatedLabelId;
    const selectedValue = String(value ?? '');
    const paperRef = useRef(null);
    const measureFrameRef = useRef(null);
    const [overflowCounts, setOverflowCounts] = useState({ above: 0, below: 0 });

    const measureOverflow = useCallback(() => {
        const paper = paperRef.current;
        if (!paper) return;
        const items = Array.from(paper.querySelectorAll('[data-portfolio-menu-item="true"]'));
        const viewportTop = paper.scrollTop;
        const viewportBottom = viewportTop + paper.clientHeight;
        const above = items.filter((item) => item.offsetTop < viewportTop - 1).length;
        const below = items.filter((item) => item.offsetTop + item.offsetHeight > viewportBottom + 1).length;
        setOverflowCounts((current) => current.above === above && current.below === below
            ? current : { above, below });
    }, []);

    const scheduleMeasure = useCallback(() => {
        if (typeof window === 'undefined') return;
        if (measureFrameRef.current != null) window.cancelAnimationFrame(measureFrameRef.current);
        measureFrameRef.current = window.requestAnimationFrame(() => {
            measureFrameRef.current = null;
            measureOverflow();
        });
    }, [measureOverflow]);

    useEffect(() => {
        if (open) scheduleMeasure();
        else {
            setOverflowCounts((current) => current.above || current.below ? { above: 0, below: 0 } : current);
            if (measureFrameRef.current != null) {
                window.cancelAnimationFrame(measureFrameRef.current);
                measureFrameRef.current = null;
            }
        }
    }, [open, groups, scheduleMeasure]);

    useEffect(() => {
        if (!open || typeof window === 'undefined') return undefined;
        window.addEventListener('resize', scheduleMeasure);
        return () => window.removeEventListener('resize', scheduleMeasure);
    }, [open, scheduleMeasure]);

    useEffect(() => () => {
        if (typeof window !== 'undefined' && measureFrameRef.current != null) {
            window.cancelAnimationFrame(measureFrameRef.current);
        }
    }, []);

    const optionCount = groups.reduce((count, group) => count + group.options.length, 0);
    const moreAboveLabel = overflowCounts.above > 0 ? `${overflowCounts.above} more above` : '';
    const moreBelowLabel = overflowCounts.below > 0 ? `${overflowCounts.below} more below` : '';
    const renderSelectedValue = () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography component="span" title={selectedTitle}
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedLabel}
            </Typography>
            {selectedIndicators}
        </Box>
    );
    // MUI Select inspects/clones its direct children for selection and typeahead.
    // Flatten groups here rather than inserting components around MenuItems.
    const options = groups.flatMap((group) => [
        ...(group.label ? [<ListSubheader key={`${group.id}-heading`}>{group.label}</ListSubheader>] : []),
        ...group.options.map((option) => {
            const optionValue = String(option.value);
            const isSelected = optionValue === selectedValue;
            const isPortfolio = option.variant === 'portfolio';
            return (
                <MenuItem
                    key={optionValue}
                    value={optionValue}
                    selected={isSelected}
                    disabled={option.disabled}
                    aria-label={isPortfolio ? undefined : option.label}
                    data-portfolio-menu-item={option.countOverflow === false ? undefined : 'true'}
                    onClick={() => {
                        if (option.reselectable && isSelected) onReselect?.(optionValue);
                    }}
                    sx={isPortfolio ? { alignItems: 'stretch', gap: 1, py: 1.25 } : undefined}
                >
                    {isPortfolio ? <>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                <Typography variant="body2" title={option.title}
                                    sx={{ fontWeight: isSelected ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {option.label}
                                </Typography>
                                {option.indicators}
                            </Box>
                            <Typography variant="caption" color="text.secondary"
                                sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {option.description}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <Typography variant="caption" color="text.secondary">{option.metadata}</Typography>
                                {option.status}
                                {option.badges}
                            </Box>
                        </Box>
                        <Stack direction="row" spacing={0.25} alignItems="center"
                            onMouseDown={stopActionMouseDown} onClick={stopActionClick}>
                            {option.actions}
                        </Stack>
                    </> : <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" title={option.title ?? option.label}
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {option.label}
                        </Typography>
                        {option.detail && <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', whiteSpace: 'normal' }}>{option.detail}</Typography>}
                    </Box>}
                </MenuItem>
            );
        }),
    ]);

    return <>
        {!hideTitle && <Typography variant="subtitle1" sx={{ ...sectionTitleSx, mb: 1, width: '100%' }}>
            Select Optimized Portfolio
        </Typography>}
        <FormControl fullWidth margin={compact ? 'dense' : 'normal'} sx={(theme) => {
            const controlBg = unsaved
                ? alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.22 : 0.15)
                : theme.palette.background.paper;
            return {
                ...getAnalyticsUnsavedControlStyle(theme, controlBg, unsaved),
                margin: 0,
                '& .MuiOutlinedInput-root': getAnalyticsUnsavedControlStyle(theme, controlBg, unsaved),
            };
        }}>
            <InputLabel id={inputLabelId}>{label}</InputLabel>
            <Select labelId={inputLabelId} value={selectedValue} label={label} disabled={disabled}
                onChange={(event) => onSelect(event.target.value)} open={open} onOpen={onOpen} onClose={onClose}
                variant="outlined" size={compact ? 'small' : 'medium'} renderValue={renderSelectedValue}
                MenuProps={{ PaperProps: {
                    ref: paperRef,
                    onScroll: scheduleMeasure,
                    'data-more-above': moreAboveLabel,
                    'data-more-below': moreBelowLabel,
                    sx: [{
                        maxHeight: 420,
                        maxWidth: 360,
                        position: 'relative',
                        overflowY: 'auto',
                        '&::before': {
                            content: 'attr(data-more-above)',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 2,
                            py: 0.45,
                            px: 1.5,
                            textAlign: 'center',
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: 0.2,
                            color: 'text.secondary',
                            pointerEvents: 'none',
                            background: APP_STYLE_PALETTE.surface,
                            borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                            opacity: overflowCounts.above > 0 ? 1 : 0,
                            transform: overflowCounts.above > 0 ? 'translateY(0)' : 'translateY(-100%)',
                            transition: 'opacity 120ms ease, transform 120ms ease',
                        },
                        '&::after': {
                            content: 'attr(data-more-below)',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            zIndex: 2,
                            py: 0.45,
                            px: 1.5,
                            textAlign: 'center',
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: 0.2,
                            color: 'text.secondary',
                            pointerEvents: 'none',
                            background: APP_STYLE_PALETTE.surface,
                            borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                            opacity: overflowCounts.below > 0 ? 1 : 0,
                            transform: overflowCounts.below > 0 ? 'translateY(0)' : 'translateY(100%)',
                            transition: 'opacity 120ms ease, transform 120ms ease',
                        },
                    }, analyticsMenuPaperSx],
                } }}>
                {options}
                {optionCount === 0 && emptyLabel != null && <MenuItem disabled>{emptyLabel}</MenuItem>}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    </>;
}
